import { useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { Layer, LineLayer, MapRef, Source } from 'react-map-gl';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetBlocksQuery } from '../../../../../services/entities/impl/blfApi/endpoints/blocks';
import {
  convertOrientationsToFeatureCollection,
  ensureLayerExists,
} from '../../../../map/mapUtils';
import {
  MapOnboardLayerEnum,
  selectCurrOnboardBlockID,
  selectShowSurroundingBlocksOnMap,
} from '../../../onboardingSlice';
import { OnboardLayerExistenceDict } from '../hooks/useCheckLayersOnboarding';
import useLayerOrderOnboarding from '../hooks/useLayerOrderOnboarding';

interface Props {
  mapRef: MapRef | null;
  layerExistenceDict: OnboardLayerExistenceDict;
}

const lineWidth = 6;

const BlockOrientationLayer: React.FC<Props> = ({
  mapRef,
  layerExistenceDict,
}) => {
  const theme = useTheme();
  const currOnboardBlockID = useAppSelector(selectCurrOnboardBlockID);
  const showSurroundingBlocksOnMap = useAppSelector(
    selectShowSurroundingBlocksOnMap
  );
  const layerBeforeId = useLayerOrderOnboarding(
    MapOnboardLayerEnum.blockOrientations,
    layerExistenceDict
  );

  // Show either all blocks or only the current block depending on the showSurroundingBlocksOnMap state (if showing all blocks in the database slows down map we can filter by ranch, but it's working well with all for now).
  const { currentData: blocks } = useGetBlocksQuery(undefined, {
    selectFromResult: ({ data }) => {
      if (showSurroundingBlocksOnMap) return { currentData: data };
      return {
        currentData: data?.filter((block) => block.id === currOnboardBlockID),
      };
    },
  });

  // Convert blocks to GeoJSON FeatureCollection for orientations
  const orientationFeatureCollection = useMemo(() => {
    return convertOrientationsToFeatureCollection(blocks || []);
  }, [blocks]);

  // Style for the xNumberOrientation line
  const xNumberOrientationStyle: LineLayer = {
    id: MapOnboardLayerEnum.blockOrientations, // use base layer id for layer ordering logic
    type: 'line',
    paint: {
      'line-color': theme.colors.darkTheme.purple,
      'line-opacity': 1,
      'line-width': lineWidth,
    },
    filter: ['==', ['get', 'id'], 'xNumberOrientation'], // Filter to apply this style only to xNumberOrientation lines
  };

  // Style for the yRowOrientation line
  const yRowOrientationStyle: LineLayer = {
    id: `${MapOnboardLayerEnum.blockOrientations}-yRow`,
    type: 'line',
    paint: {
      'line-color': theme.colors.darkTheme.green,
      'line-opacity': 1,
      'line-width': lineWidth,
    },
    filter: ['==', ['get', 'id'], 'yRowOrientation'], // Filter to apply this style only to yRowOrientation lines
  };

  return (
    <Source
      key={MapOnboardLayerEnum.blockOrientations}
      id={MapOnboardLayerEnum.blockOrientations}
      type='geojson'
      data={orientationFeatureCollection}
    >
      <Layer
        key={MapOnboardLayerEnum.blockOrientations} // use base layer id for layer ordering logic
        {...xNumberOrientationStyle}
        beforeId={ensureLayerExists(mapRef, layerBeforeId)}
      />
      <Layer
        key={`${MapOnboardLayerEnum.blockOrientations}-yRow`}
        {...yRowOrientationStyle}
        beforeId={ensureLayerExists(mapRef, layerBeforeId)}
      />
    </Source>
  );
};

export default BlockOrientationLayer;
